import { useEffect, useMemo, useState } from "react";
import { Pencil, RotateCcw, Sparkles } from "lucide-react";
import { Glass } from "./SuiteLayout";

const field = "w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white focus:border-purple-400 outline-none";

function Row({ r, onChange }) {
  const url = useMemo(() => URL.createObjectURL(r.blob), [r.blob]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  const set = (patch) => onChange(r.index, { ...r.ai, ...patch });
  return (
    <div className="p-4 grid sm:grid-cols-[96px_1fr] gap-4" data-testid={`ai-edit-row-${r.index}`}>
      <img src={url} alt={r.ai.alt_text} className="w-24 h-24 object-cover rounded-lg border border-white/10" />
      <div className="space-y-2 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="font-mono text-[11px] text-slate-500 truncate">{r.name}</p>
          {r.edited && <button onClick={() => onChange(r.index, r.original, true)} className="text-[11px] text-slate-400 hover:text-white flex items-center gap-1" data-testid={`ai-edit-reset-${r.index}`}><RotateCcw size={11} /> Reset</button>}
        </div>
        <input value={r.ai.title} maxLength={120} onChange={(e) => set({ title: e.target.value })} className={field} placeholder="Title" data-testid={`ai-edit-title-${r.index}`} />
        <input value={r.ai.tags.join(", ")} onChange={(e) => set({ tags: e.target.value.split(",").map((t) => t.trimStart()).slice(0, 15) })}
          className={`${field} font-mono text-xs`} placeholder="tags, comma, separated" data-testid={`ai-edit-tags-${r.index}`} />
        <textarea value={r.ai.alt_text} maxLength={250} rows={2} onChange={(e) => set({ alt_text: e.target.value })} className={`${field} resize-none`} placeholder="Alt text" data-testid={`ai-edit-alt-${r.index}`} />
      </div>
    </div>
  );
}

export function AiCopyEditor({ results, onChange }) {
  const [open, setOpen] = useState(true);
  const items = results.filter((r) => r.ai);
  if (!items.length) return null;
  const edited = items.filter((r) => r.edited).length;
  return (
    <Glass className="mt-6" data-testid="ai-copy-editor">
      <button onClick={() => setOpen(!open)} className="w-full px-5 py-4 flex items-center justify-between text-left" data-testid="ai-copy-editor-toggle">
        <span className="flex items-center gap-2 text-white font-semibold"><Sparkles size={16} className="text-purple-300" /> Review AI copy <span className="text-xs font-mono text-slate-400">{items.length} products{edited ? ` · ${edited} edited` : ""}</span></span>
        <Pencil size={14} className="text-slate-400" />
      </button>
      {open && (
        <>
          <p className="px-5 pb-3 text-xs text-slate-400">Edits are saved into each photo's EXIF, product-descriptions.csv and shopify-products.csv when you download the ZIP.</p>
          <div className="max-h-[560px] overflow-auto divide-y divide-white/5 border-t border-white/5">
            {items.map((r) => <Row key={r.index} r={r} onChange={onChange} />)}
          </div>
        </>
      )}
    </Glass>
  );
}
